/**
 *
 *   Copyright ou © ou Copr. Université de Lorraine, (2025)
 *
 *   Direction du Numérique de l'Université de Lorraine - SIED
 *
 *   Ce logiciel est un programme informatique servant à rendre accessible
 *   sur mobile et sur internet l'application ULEP (University Language
 *   Exchange Programme) aux étudiants et aux personnels des universités
 *   parties prenantes.
 *
 *   Ce logiciel est régi par la licence CeCILL 2.1, soumise au droit français
 *   et respectant les principes de diffusion des logiciels libres. Vous pouvez
 *   utiliser, modifier et/ou redistribuer ce programme sous les conditions
 *   de la licence CeCILL telle que diffusée par le CEA, le CNRS et INRIA
 *   sur le site "http://cecill.info".
 *
 *   En contrepartie de l'accessibilité au code source et des droits de copie,
 *   de modification et de redistribution accordés par cette licence, il n'est
 *   offert aux utilisateurs qu'une garantie limitée. Pour les mêmes raisons,
 *   seule une responsabilité restreinte pèse sur l'auteur du programme, le
 *   titulaire des droits patrimoniaux et les concédants successifs.
 *
 *   À cet égard, l'attention de l'utilisateur est attirée sur les risques
 *   associés au chargement, à l'utilisation, à la modification et/ou au
 *   développement et à la reproduction du logiciel par l'utilisateur étant
 *   donné sa spécificité de logiciel libre, qui peut le rendre complexe à
 *   manipuler et qui le réserve donc à des développeurs et des professionnels
 *   avertis possédant des connaissances informatiques approfondies. Les
 *   utilisateurs sont donc invités à charger et à tester l'adéquation du
 *   logiciel à leurs besoins dans des conditions permettant d'assurer la
 *   sécurité de leurs systèmes et/ou de leurs données et, plus généralement,
 *   à l'utiliser et à l'exploiter dans les mêmes conditions de sécurité.
 *
 *   Le fait que vous puissiez accéder à cet en-tête signifie que vous avez
 *   pris connaissance de la licence CeCILL 2.1, et que vous en avez accepté les
 *   termes.
 *
 */

import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import SchoolIcon from '@mui/icons-material/School';
import SettingsIcon from '@mui/icons-material/Settings';
import { Divider } from '@mui/material';
import Box from '@mui/material/Box';
import { useState } from 'react';
import { Menu, useCreatePath, useGetIdentity, usePermissions, useTranslate } from 'react-admin';
import { Role } from '../../entities/Administrator';
import useLimitedFeatures from '../../utils/useLimitedFeatures';
import useCurrentPathname from './useCurrentPathname';

export type LinkPage = {
    resource: string;
    type: string;
    id?: string;
    label: string;
    role?: string;
};

export type SubMenusType = {
    [key: string]: LinkPage[];
};

export const subMenus: SubMenusType = {
    configuration: [
        {
            resource: 'users/administrators',
            type: 'list',
            label: 'administrators.tabLabel',
        },
        {
            resource: 'instance',
            id: 'config',
            type: 'show',
            label: 'instance.tabLabel',
            role: Role.SUPER_ADMIN,
        },
        {
            resource: 'languages',
            type: 'list',
            label: 'languages.tabLabel',
            role: Role.SUPER_ADMIN,
        },
        {
            resource: 'languages/requests',
            type: 'list',
            label: 'suggested_languages.tabLabel',
            role: Role.SUPER_ADMIN,
        },
        {
            resource: 'languages/requests/count',
            type: 'list',
            label: 'count_suggested_languages.tabLabel',
            role: Role.SUPER_ADMIN,
        },
        {
            resource: 'interests/categories',
            type: 'list',
            label: 'interest_categories.tabLabel',
            role: Role.SUPER_ADMIN,
        },
        {
            resource: 'activities/categories',
            type: 'list',
            label: 'activities_categories.tabLabel',
            role: Role.SUPER_ADMIN,
        },
        {
            resource: 'countries',
            type: 'list',
            label: 'countries.tabLabel',
            role: Role.SUPER_ADMIN,
        },
    ],
    reports: [
        {
            resource: 'reports',
            type: 'list',
            label: 'reports.tabLabel',
        },
        {
            resource: 'reports/categories',
            type: 'list',
            label: 'report_categories.tabLabel',
            role: Role.SUPER_ADMIN,
        },
    ],
    universities: [
        {
            resource: 'universities',
            type: 'list',
            label: 'universities.tabLabel',
        },
        {
            resource: 'campus',
            type: 'list',
            label: 'campus.tabLabel',
        },
    ],
};

const RA_ACTIVE_CLASS = 'RaMenuItemLink-active';

const manageSubMenusActiveClass = (aElement: any, currentPathname: string, subMenuName: string) => {
    const resources = subMenus[subMenuName].map((link) => link.resource);
    const currentResource = resources.filter((resource) => currentPathname.includes(resource));

    if (aElement && currentResource.length !== 0) {
        aElement.classList.add(RA_ACTIVE_CLASS);
    } else if (aElement) {
        aElement.classList.remove(RA_ACTIVE_CLASS);
    }
};

const manageUniversitySubMenuActiveClass = (
    universityRef: HTMLDivElement,
    currentPathname: string,
    subMenuName: string
) => {
    const aElement = universityRef.firstElementChild;
    manageSubMenusActiveClass(aElement, currentPathname, subMenuName);
};

const manageConfigSubMenuActiveClass = (configRef: HTMLDivElement, currentPathname: string, subMenuName: string) => {
    const aElement = configRef.firstElementChild;
    manageSubMenusActiveClass(aElement, currentPathname, subMenuName);

    if (currentPathname.includes('activities/themes') || currentPathname.includes('interests/')) {
        aElement?.classList.add(RA_ACTIVE_CLASS);
    }
};

const manageProfilesPathActiveClass = (profilesRef: HTMLDivElement, currentPathname: string) => {
    const aElement = profilesRef.firstElementChild;

    if (currentPathname.includes('profiles/with-tandems')) {
        aElement?.classList.remove(RA_ACTIVE_CLASS);
    } else if (currentPathname.includes('profiles')) {
        aElement?.classList.add(RA_ACTIVE_CLASS);
    }
};

const manageActivitiesPathActiveClass = (activitiesRef: HTMLDivElement, currentPathname: string) => {
    const aElement = activitiesRef.firstElementChild;

    if (currentPathname.includes('activities/categories')) {
        aElement?.classList.remove(RA_ACTIVE_CLASS);
    } else if (currentPathname.includes('activities') && !currentPathname.includes('activities/themes')) {
        aElement?.classList.add(RA_ACTIVE_CLASS);
    }
};

const CustomMenu = () => {
    const { data } = useGetIdentity();
    const { permissions } = usePermissions();
    const translate = useTranslate();
    const createPath = useCreatePath();
    const currentPathname = useCurrentPathname();
    const [universitiesRef, setUniversitiesRef] = useState<HTMLDivElement>();
    const [configurationRef, setConfigurationRef] = useState<HTMLDivElement>();
    const [profilesRef, setProfilesRef] = useState<HTMLDivElement>();
    const [activitiesRef, setActivitiesRef] = useState<HTMLDivElement>();
    const limitedFeatures = useLimitedFeatures();

    if (universitiesRef) {
        manageUniversitySubMenuActiveClass(universitiesRef, currentPathname, 'universities');
    }
    if (configurationRef) {
        manageConfigSubMenuActiveClass(configurationRef, currentPathname, 'configuration');
    }
    if (profilesRef) {
        manageProfilesPathActiveClass(profilesRef, currentPathname);
    }
    if (activitiesRef) {
        manageActivitiesPathActiveClass(activitiesRef, currentPathname);
    }

    return (
        <Menu sx={{ display: 'flex' }}>
            <Box ref={(newRef: HTMLDivElement) => setProfilesRef(newRef)} sx={{ marginTop: 0 }}>
                <Menu.ResourceItem name="profiles" />
            </Box>
            <Menu.ResourceItem name="profiles/with-tandems-profiles" />
            {!limitedFeatures && (
                // Note: div is mandatory to group these Menu.Item as Fragment throw an error from MUI component
                <div>
                    <Menu.ResourceItem name="chat" />
                    <Menu.ResourceItem name="news" />
                    <Menu.ResourceItem name="events" />
                    <Box ref={(newRef: HTMLDivElement) => setActivitiesRef(newRef)} sx={{ marginTop: 0 }}>
                        <Menu.ResourceItem name="activities" />
                    </Box>
                </div>
            )}
            {permissions.checkRole(Role.MANAGER) && data && data.universityId && (
                // Note: div is mandatory to group these Menu.Item as Fragment throw an error from MUI component
                <div>
                    <Menu.Item
                        leftIcon={<SchoolIcon />}
                        primaryText={translate('universities.label')}
                        to={createPath({ resource: 'universities', id: data.universityId, type: 'show' })}
                    />
                    <Menu.Item
                        leftIcon={<FormatQuoteIcon />}
                        primaryText={translate('editos.label')}
                        to={createPath({ resource: 'editos', id: data.universityId, type: 'show' })}
                    />
                </div>
            )}
            {permissions.checkRole(Role.SUPER_ADMIN) && (
                // Note: div is mandatory to group these Menu.Item as Fragment throw an error from MUI component
                <div>
                    <Box ref={(newRef: HTMLDivElement) => setUniversitiesRef(newRef)}>
                        <Menu.ResourceItem name="universities" />
                    </Box>
                    {!limitedFeatures && <Menu.ResourceItem name="editos" />}
                    <Menu.ResourceItem name="objectives" />
                    <Menu.ResourceItem name="proficiency/questions" />
                </div>
            )}
            <Menu.ResourceItem name="reports" />
            {!permissions.checkRole(Role.ANIMATOR) && (
                // Note: div is mandatory to group these Menu.Item as Fragment throw an error from MUI component
                <div>
                    <Divider sx={{ margin: '0 !important' }} />
                    <Box ref={(newRef: HTMLDivElement) => setConfigurationRef(newRef)}>
                        <Menu.Item
                            leftIcon={<SettingsIcon />}
                            primaryText={translate('instance.label')}
                            to="/users/administrators"
                        />
                    </Box>
                </div>
            )}
        </Menu>
    );
};

export default CustomMenu;
